import React, {useEffect, useRef, useState} from 'react';
import L, {DivIcon, LatLngBounds, Map as LeafletMap, Marker as LeafletMarker} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import BasketPageSection from "./BasketPageSection";
import DeliveryLocations from "../../../SubscriptionWizard/components/FormulaStep/components/DeliveryLocations";
import CallToAction from "../CallToAction";
import {
  DeliveryLocationDetail,
  deliveryLocations
} from "../../../../shared/utils/deliveryLocation.utils";

interface DeliverySectionProps {
}

const MapContainer = styled.div`
  position: relative;
  height: 620px;
  width: 100%;

  @media only screen and (max-width: 991px) {
    height: 420px;
  }

  .leaflet-container {
    height: 100%;
    width: 100%;
    font-family: inherit;
  }

  .delivery-location-marker {
    width: 34px;
    height: 34px;
    border-radius: 50% 50% 50% 0;
    background: var(--green);
    border: 3px solid #ffffff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
    transform: rotate(-45deg);
    transition: width 160ms ease, height 160ms ease, background-color 160ms ease, box-shadow 160ms ease;
  }

  .delivery-location-marker:after {
    content: '';
    position: absolute;
    inset: 8px;
    border-radius: 50%;
    background: #ffffff;
  }

  .delivery-location-marker.active {
    width: 46px;
    height: 46px;
    background: var(--orange);
    box-shadow: 0 0 0 10px rgba(238, 127, 0, 0.24), 0 10px 26px rgba(0, 0, 0, 0.28);
  }

  .delivery-location-label {
    position: absolute;
    left: 50%;
    bottom: 48px;
    transform: translateX(-50%);
    white-space: nowrap;
    padding: 7px 11px;
    border-radius: 999px;
    background: #ffffff;
    color: var(--green);
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
    pointer-events: none;
  }

  .delivery-location-marker-icon {
    background: transparent;
    border: 0;
  }

  .leaflet-control-attribution {
    font-size: 10px;
  }
`;

const createMarkerIcon = (location: DeliveryLocationDetail, active: boolean): DivIcon => {
  return L.divIcon({
    className: 'delivery-location-marker-icon',
    iconSize: active ? [46, 46] : [34, 34],
    iconAnchor: active ? [23, 46] : [17, 34],
    html: `
      <div class="delivery-location-marker ${active ? 'active' : ''}">
        ${active ? `<div class="delivery-location-label">${location.name}, ${location.city}</div>` : ''}
      </div>
    `,
  });
};

const DeliveryMap = ({activeLocation}: { activeLocation: DeliveryLocationDetail | null }) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Array<{ location: DeliveryLocationDetail; marker: LeafletMarker }>>([]);

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) {
      return;
    }

    const map = L.map(mapElementRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    const bounds = new LatLngBounds([]);

    markersRef.current = deliveryLocations.map(location => {
      const marker = L.marker([location.lat, location.lng], {
        icon: createMarkerIcon(location, false),
        title: `${location.name}, ${location.city}`,
      }).addTo(map);

      marker.bindPopup(`
        <strong>${location.name}</strong><br/>
        ${location.address}<br/>
        ${location.city}
      `);

      bounds.extend([location.lat, location.lng]);

      return {
        location,
        marker,
      };
    });

    map.fitBounds(bounds, {
      padding: [34, 34],
      maxZoom: 11,
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, []);

  useEffect(() => {
    markersRef.current.forEach(({location, marker}) => {
      const active = activeLocation?.value === location.value;

      marker.setIcon(createMarkerIcon(location, active));

      if (active) {
        marker.setZIndexOffset(1000);
      } else {
        marker.setZIndexOffset(0);
      }
    });

    if (activeLocation && mapRef.current) {
      mapRef.current.panTo([activeLocation.lat, activeLocation.lng], {
        animate: true,
        duration: 0.35,
      });
    }
  }, [activeLocation]);

  return <div ref={mapElementRef} className="leaflet-container"/>;
};

const DeliverySection = (props: DeliverySectionProps) => {
  const [activeLocation, setActiveLocation] = useState<DeliveryLocationDetail | null>(null);

  return (
    <>
      <BasketPageSection image='pakket-omloop.jpg' gradientPosition='right' title='Kies zelf'
                         subTitle='een afhaalpunt in de buurt'>
        <div className="grid align-items-center">
          <div className="col-12 lg:col-6">
            <div
              className="inline-flex align-items-center gap-2 px-3 py-2 border-round-3xl mb-3"
              style={{background: 'var(--green-20)'}}
            >
              <i className="pi pi-map-marker"/>
              <span className="font-medium">Afhalen in je buurt</span>
            </div>

            <div className="color-orange text-xl font-bold mb-3">
              Altijd een afhaalpunt in je buurt
            </div>

            <p className="text-lg line-height-3">
              Onze pakketten worden op <strong>vrijdag</strong> in de namiddag geleverd in het afhaalpunt.
            </p>

            <p className="line-height-3 mb-4">
              Jij kiest zelf je vaste afhaalpunt. Wil je toch een keer je pakket op een ander afhaalpunt afhalen,
              dan kan je dit eenvoudig zelf doorgeven via de webapp.
            </p>

            <DeliveryLocations
              static={true}
              hideAddress
              compact
              onMouseEnter={setActiveLocation}
              onMouseLeave={() => setActiveLocation(null)}
            />
          </div>

          <div className="col-12 lg:col-6">
            <div className="surface-card border-1 border-200 border-round-2xl shadow-2 overflow-hidden">
              <MapContainer>
                <DeliveryMap activeLocation={activeLocation}/>
              </MapContainer>
            </div>
          </div>
        </div>
      </BasketPageSection>
      <CallToAction type={'pakketten'} label={'Schrijf mij in voor een flex-pakket'}/>
    </>
  );
};

export default DeliverySection;
